import { Component } from '@theme/component';

const sources = new Set();
let paused = false;
try { paused = sessionStorage.getItem('qr-seasonal-motion-paused') === 'true'; } catch {}
let screen;
let owner;
let abort;
let observer;
let motion;
let frame = null;
let rebuild = false;
let intersection;
let localVisible = true;

const isRendered = element => !element.closest('[hidden], [inert], [aria-hidden="true"]') && element.getClientRects().length > 0;
const dialogIsOpen = () => [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some(element => isRendered(element) && getComputedStyle(element).visibility !== 'hidden');

function schedule() {
  if (screen && frame === null) frame = requestAnimationFrame(update);
}

function update() {
  frame = null;
  const eligible = [...sources].filter(source => source.isConnected && isRendered(source) && (source.dataset.motionScope !== 'hero' || source.closest('[data-qr-seasonal-atmosphere-host]')));
  const next = eligible.find(source => source.closest('.qrc-product-proof, [data-qr-collection-family]')) || eligible[0];
  const feature = next?.dataset.season === 'halloween' && next.closest('[data-qr-seasonal-spotlight]')
    ? next.closest('[data-qr-seasonal-atmosphere-host]')
    : null;
  const hero = next?.dataset.motionScope === 'hero' ? next.closest('[data-qr-seasonal-atmosphere-host]') : null;
  const container = hero || feature || document.body;
  if (screen.parentElement !== container) container.append(screen);
  const screenClass = hero ? 'qr-seasonal-screen qr-seasonal-screen--hero' : feature ? 'qr-seasonal-screen qr-seasonal-screen--feature' : 'qr-seasonal-screen';
  const portalScope = hero ? 'hero' : feature ? 'feature' : 'screen';
  if (screen.dataset.motionScope !== portalScope) screen.dataset.motionScope = portalScope;
  const scopeChanged = screen.className !== screenClass;
  if (scopeChanged) screen.className = screenClass;
  if (next !== owner || rebuild || scopeChanged || (next && (screen.dataset.season !== next.dataset.season || (screen.dataset.motionEdition || '') !== (next.dataset.motionEdition || '')))) {
    owner = next;
    rebuild = false;
    intersection?.disconnect();
    localVisible = !hero || !intersection;
    if (hero && intersection) intersection.observe(owner);
    screen.replaceChildren();
    if (owner) {
      const decoration = owner.querySelector('.qr-seasonal-motion-decoration')?.cloneNode(true);
      const button = owner.querySelector('button')?.cloneNode(true);
      if (decoration) screen.append(decoration);
      if (button) {
        button.className = 'qr-seasonal-screen-toggle';
        button.hidden = false;
        button.removeAttribute('aria-pressed');
        screen.append(button);
      }
      screen.dataset.season = owner.dataset.season;
      screen.dataset.motionEdition = owner.dataset.motionEdition || '';
    }
  }
  const hidden = !owner || motion.matches || document.hidden || dialogIsOpen() || Boolean(hero && !localVisible);
  if (screen.hidden !== hidden) screen.hidden = hidden;
  const running = !hidden && !paused;
  if (screen.dataset.running !== String(running)) screen.dataset.running = String(running);
  for (const source of sources) {
    const value = String(source === owner && running);
    if (source.dataset.running !== value) source.dataset.running = value;
  }
  const button = screen.querySelector('button');
  if (button) {
    const label = paused ? button.dataset.playLabel : button.dataset.pauseLabel;
    if (button.textContent !== label) button.textContent = label;
    if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
    if (button.getAttribute('title') !== label) button.setAttribute('title', label);
  }
}

function start() {
  abort = new AbortController();
  const { signal } = abort;
  motion = matchMedia('(prefers-reduced-motion: reduce)');
  if (typeof IntersectionObserver !== 'undefined') intersection = new IntersectionObserver(entries => {
    const entry = [...entries].reverse().find(item => item.target === owner);
    if (entry && localVisible !== entry.isIntersecting) {
      localVisible = entry.isIntersecting;
      schedule();
    }
  });
  screen = document.createElement('div');
  screen.className = 'qr-seasonal-screen';
  screen.dataset.running = 'false';
  screen.hidden = true;
  document.body.append(screen);
  screen.addEventListener('click', event => {
    if (!(event.target instanceof Element) || !event.target.closest('.qr-seasonal-screen-toggle')) return;
    paused = !paused;
    try { sessionStorage.setItem('qr-seasonal-motion-paused', String(paused)); } catch {}
    schedule();
  }, { signal });
  motion.addEventListener('change', schedule, { signal });
  document.addEventListener('visibilitychange', schedule, { signal });
  document.addEventListener('qr:seasonal-motion', schedule, { signal });
  document.addEventListener('shopify:section:load', schedule, { signal });
  window.addEventListener('resize', schedule, { passive: true, signal });
  observer = new MutationObserver(records => {
    if (records.some(record => !screen.contains(record.target) && record.target !== screen)) schedule();
  });
  observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'inert', 'open', 'aria-hidden', 'data-active-season', 'data-season', 'data-motion-scope', 'data-motion-edition'] });
  schedule();
}

function stop() {
  abort?.abort();
  observer?.disconnect();
  intersection?.disconnect();
  intersection = null;
  localVisible = true;
  if (frame !== null) cancelAnimationFrame(frame);
  frame = null;
  screen?.remove();
  screen = null;
  owner = null;
  rebuild = false;
}

class SeasonalAtmosphere extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (sources.has(this)) return;
    sources.add(this);
    if (sources.size === 1) start();
    schedule();
  }
  updatedCallback() {
    super.updatedCallback();
    if (owner === this) rebuild = true;
    schedule();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    sources.delete(this);
    this.dataset.running = 'false';
    if (sources.size === 0) stop();
    else schedule();
  }
}

if (!customElements.get('qr-seasonal-atmosphere')) customElements.define('qr-seasonal-atmosphere', SeasonalAtmosphere);
